import { useRef } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { Button } from "@zapier/design-system-beta";
import {
  SkeletonText,
  Spinner,
  SkeletonBlock,
  Icon,
  Text,
  Alert,
  AlertDescription,
} from "@zapier/design-system";
import { Node, NodeProps } from "@xyflow/react";
import {
  aiSuggestedEditsAtomFamily,
  interfacesAppAtom,
  tablesAppAtom,
  ZapNodeData,
  aiSuggestedEditsLoadingAtomFamily,
  zslExecutionLoadingAtomFamily,
  cancelAiSuggestCommandAtomFamily,
  parseAdditionCanvasEditsToNodeData,
  replaceDefaultWithAiSuggestedCommand,
  suggestedNodeHeightsAtomFamily,
  SUGGESTED_NODE_HEIGHT_FALLBACK,
  zslExecutionErrorAtomFamily,
  aiSuggestionErrorAtomFamily,
  isDefaultNode,
  NodeData,
  DefaultNodeData,
  isZapNode,
} from "@zapier/canvas-state";

import { DefaultNodeContent } from "../Canvas/nodeTypes/DefaultNode/DefaultNodeContent";
import { ZapNodeContent } from "../Canvas/nodeTypes/ZapNode/ZapNodeContent";
import styles from "./SmartFlyout.module.css";

type ButtonProps = {
  nodeId: string;
};

function StopGenerationButton({ nodeId }: ButtonProps) {
  const cancelAiSuggest = useSetAtom(cancelAiSuggestCommandAtomFamily(nodeId));

  return (
    <Button variant="secondary" size="small" onPress={cancelAiSuggest}>
      <Icon name="statusStop" />
      Stop generation
    </Button>
  );
}

type HeaderProps = {
  isLoading?: boolean;
};

function Header({ isLoading }: HeaderProps) {
  return (
    <span className={styles.label}>
      {isLoading && (
        <div className={styles.spinnerWrapper}>
          <Spinner size="small" />
        </div>
      )}
      <Text type="MinimalPrint1" color="TextWeakest">
        {isLoading ? "AI suggestion loading..." : "AI suggestion"}
      </Text>
    </span>
  );
}

type Props = {
  nodeId: string;
};

export function SmartFlyout({ nodeId }: Props) {
  const replaceDefaultWithAiSuggested = useSetAtom(
    replaceDefaultWithAiSuggestedCommand,
  );
  const suggestion = useAtomValue(aiSuggestedEditsAtomFamily(nodeId));
  const isLoading = useAtomValue(aiSuggestedEditsLoadingAtomFamily(nodeId));
  const isZslExecuting = useAtomValue(zslExecutionLoadingAtomFamily(nodeId));
  const cancelAiSuggest = useSetAtom(cancelAiSuggestCommandAtomFamily(nodeId));
  const zslExecutionError = useAtomValue(zslExecutionErrorAtomFamily(nodeId));
  const aiSuggestionError = useAtomValue(aiSuggestionErrorAtomFamily(nodeId));
  const setNodeHeights = useSetAtom(suggestedNodeHeightsAtomFamily(nodeId));

  const flyoutRef = useRef<HTMLDivElement | null>(null);

  const handleReplace = async () => {
    if (flyoutRef.current) {
      const suggestedNodes = flyoutRef.current.querySelectorAll(
        "[id^='suggestion-']",
      );

      const heights = Array.from(suggestedNodes).map((nodeElement) => {
        try {
          const height = nodeElement.getBoundingClientRect().height;
          return Math.round(height);
        } catch (error) {
          return SUGGESTED_NODE_HEIGHT_FALLBACK; // Fallback to default height
        }
      }) as number[];

      setNodeHeights(heights);
    }

    try {
      await replaceDefaultWithAiSuggested(nodeId);
      setNodeHeights([]); // Clear heights after replacement
    } catch (error) {
      // Error handling is now done in replaceDefaultWithAiSuggestedCommand
      console.error("Failed to replace suggestion:", error);
    }
  };

  const tablesApp = useAtomValue(tablesAppAtom);
  const interfacesApp = useAtomValue(interfacesAppAtom);

  if (isLoading && !suggestion) {
    return (
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <div className={styles.suggestionBox}>
            <Header isLoading={isLoading} />
            <div>
              <div className={styles.suggestionBoxSkeleton}>
                <div className={styles.suggestionBoxSkeletonHeader}>
                  <SkeletonText
                    size="body"
                    lines={3}
                    color={"GrayWarm3"}
                    width={164}
                  />
                </div>
                <div className={styles.suggestionBoxSkeletonFooter}>
                  <div className={styles.suggestionBoxSkeletonFooterLeft}>
                    <SkeletonBlock
                      borderRadius={"small"}
                      height={15}
                      width={15}
                      color={"GrayWarm3"}
                    />
                    <SkeletonText
                      size="body"
                      lines={1}
                      color={"GrayWarm3"}
                      width={60}
                    />
                  </div>
                  <div className={styles.suggestionBoxSkeletonFooterRight}>
                    <SkeletonBlock
                      borderRadius={"small"}
                      height={15}
                      width={39}
                      color={"GrayWarm3"}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.actions}>
              <StopGenerationButton nodeId={nodeId} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.wrapper}>
        <div className={styles.suggestionBox}>
          <Header isLoading={isLoading} />
          <div className={styles.suggestionBoxContent} ref={flyoutRef}>
            {suggestion?.additions.flatMap((addition, additionIndex) => {
              const nodes = parseAdditionCanvasEditsToNodeData(addition, {
                tablesApp: tablesApp!,
                interfacesApp: interfacesApp!,
              }) as Array<Node>;

              return nodes.map((nodeData: Node<NodeData>, nodeIndex) => {
                const suggestedNodeId = `suggestion-${additionIndex}-${nodeIndex}`;

                if (isDefaultNode(nodeData)) {
                  return (
                    <div key={suggestedNodeId} id={suggestedNodeId}>
                      <DefaultNodeContent
                        {...(nodeData as unknown as NodeProps<
                          Node<DefaultNodeData>
                        >)}
                        isViewOnlyMode={true}
                        className={styles.suggestionNode}
                      />
                    </div>
                  );
                } else if (isZapNode(nodeData)) {
                  return (
                    <div key={suggestedNodeId} id={suggestedNodeId}>
                      <ZapNodeContent
                        {...(nodeData as unknown as NodeProps<
                          Node<ZapNodeData>
                        >)}
                        isViewOnlyMode={true}
                        className={styles.suggestionNode}
                      />
                    </div>
                  );
                }
                return null;
              });
            })}
          </div>
        </div>
        {(zslExecutionError || aiSuggestionError) && (
          <div className={styles.error}>
            <Alert variant="compact" status="warning">
              <AlertDescription>
                {aiSuggestionError?.message ||
                  zslExecutionError?.message ||
                  "Whoops! We can't build that suggestion right now. Please edit the prompt and try again."}
              </AlertDescription>
            </Alert>
          </div>
        )}
        <div className={styles.actions}>
          {isLoading ? (
            <StopGenerationButton nodeId={nodeId} />
          ) : (
            <>
              <Button
                size="small"
                variant="secondary"
                onPress={cancelAiSuggest}
              >
                Dismiss
              </Button>
              <Button
                size="small"
                onPress={handleReplace}
                isLoading={isZslExecuting}
              >
                Replace
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
